import { useEffect, useState, useContext, useMemo, useCallback } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
// material
import jwt_decode from 'jwt-decode'
import { alpha, styled, useTheme } from '@material-ui/core/styles'
//
import DashboardNavbar from './DashboardNavbar'
import DashboardSidebar from './DashboardSidebar'
import storage from 'utils/storage'
import user_api from 'utils/api/users'

// import { SocketContext } from 'utils/context/socket'
import { MainContext } from 'utils/context/main'
import NotificationDialog from 'components/firebase/NotificationDialog'
import NotificationComponent from 'components/firebase/Notification'
import MuiAppBar from '@mui/material/AppBar';
import { Drawer, Divider, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Box, Typography, Avatar } from '@material-ui/core';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccountPopover from './AccountPopover'
import NewFeatureDialog from 'components/newFeatureDialog'
import { Icon } from '@iconify/react'
import { useWorkmateTimein } from 'utils/context/workmateTimein';
/*import {useFirebase} from 'utils/context/firebase'*/
// import {UsersContext} from 'utils/context/users'
import React from 'react';
import { Suspense } from 'react';

// ----------------------------------------------------------------------

const APP_BAR_MOBILE = 64
const APP_BAR_DESKTOP = 92
const drawerWidth = 300;

// Detect mobile platforms - add safety checks to prevent process errors
const isReactNative = (() => {
  // Method 1: Check navigator.product (works in React Native 0.24+)
  if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
    return true;
  }
  
  // Method 2: Check for React Native specific globals
  if (typeof window !== 'undefined' && window.ReactNativeWebView) {
    return true;
  }
  
  // Method 3: Check for Metro bundler specific globals
  if (typeof global !== 'undefined' && global.__metro) {
    return true;
  }
  
  // Method 4: Check for React Native bridge
  if (typeof window !== 'undefined' && (window.postMessage && window.ReactNativeWebView)) {
    return true;
  }
  
  // Method 5: Check userAgent for React Native indicators
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    const ua = navigator.userAgent;
    if (ua.includes('ReactNative') || ua.includes('React Native')) {
      return true;
    }
  }
  
  // Method 6: Check for Platform API (React Native specific)
  try {
    if (typeof require !== 'undefined') {
      const Platform = require('react-native')?.Platform;
      if (Platform && (Platform.OS === 'ios' || Platform.OS === 'android')) {
        return true;
      }
    }
  } catch (e) {
    // Ignore errors in web environment
  }
  
  // Method 7: Check for React Native DevTools
  if (typeof window !== 'undefined' && window.__REACT_DEVTOOLS_GLOBAL_HOOK__ && window.__REACT_DEVTOOLS_GLOBAL_HOOK__.reactNative) {
    return true;
  }
  
  return false;
})();
const isMobile = typeof window !== 'undefined' && typeof navigator !== 'undefined' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const isIOS = typeof window !== 'undefined' && typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isAndroid = typeof window !== 'undefined' && typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

const RootStyle = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden',
})

const MainStyle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100vh',
  // Reduced top padding to minimize gap between header and content
  paddingTop: isReactNative ? ( isIOS ? theme.spacing(1) : theme.spacing(2))  : theme.spacing(2),
  // Reduced bottom padding for footer
  paddingBottom: theme.spacing(2),
  width: '100%',
  maxWidth: '100%',
  [theme.breakpoints.up('lg')]: {
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2), 
    paddingRight: theme.spacing(2),
  },
}));

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isQRScanRoute' })(
  ({ theme, open, isQRScanRoute }) => ({
    flexGrow: 1,
    // Add top margin for mobile to prevent header overlap with native status bar
    marginTop: isReactNative ? (isIOS ? 44 : 0) : 0, // iOS status bar: 44px, Android: 24px
    padding: theme.spacing(0.5),
    width: '100%',
    maxWidth: '100%', 
    overflowX: 'hidden',
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    // For QR scan route, use normal positioning without negative margins
    ...(isQRScanRoute ? {
      marginRight: 0,
      width: '100%'
    } : {
      [theme.breakpoints.down('sm')]: {
        marginRight: `-100%`,
        width: '100%'
      },
      [theme.breakpoints.up('sm')]: {
        marginRight: -drawerWidth,
        width: '100%'
      },
    }),
    ...(open && !isQRScanRoute && {
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginRight: 0,
      [theme.breakpoints.down('sm')]: {
        width: `calc(100% - 100%)`
      },
      [theme.breakpoints.up('sm')]: {
        width: `calc(100% - ${drawerWidth}px)`
      }
    }),
    position: 'relative',
  }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // Reduced height for less spacing
  height: isReactNative ? ( isIOS ? 40 : 56) : 56,
  ...theme.mixins.toolbar,
  justifyContent: 'flex-start',
}));

const DrawerFooter = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  height: isReactNative ? ( isIOS ? 40 : 56) : 56,
  ...theme.mixins.toolbar,
  justifyContent: 'flex-start',
}));

// Optimized AppBar with color-coded background instead of image
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isQRScanRoute'
})(({ theme, open, data, isQRScanRoute }) => {
  // Add safety checks for data
  const userRole = data?.users?.role ? JSON.parse(data.users.role) : 0;
  const isAdminOrHigher = userRole === 1 || userRole >= 3;
  
  return {
    // For QR scan route, ensure proper static positioning
    ...(isQRScanRoute && {
      position: 'static',
      transform: 'none',
      top: 'auto',
      left: 'auto',
      right: 'auto',
      zIndex: 'auto',
    }),
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    boxShadow: 'none',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    // Use color instead of background image for better performance
    // For QR scan route or when transparent would cause issues, use solid background
    backgroundColor: 'transparent',
    // Add safe area padding for mobile devices
    paddingTop: isReactNative ? (isIOS ? 44 : 0) : 0,
    ...(!isQRScanRoute && open && {
      [theme.breakpoints.down('sm')]: {
        width: `calc(100% - 100%)`,
      },
      [theme.breakpoints.up('sm')]: {
        width: `calc(100% - ${drawerWidth}px)`,
      },
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      [theme.breakpoints.down('sm')]: {
        marginRight: `100%`,
      },
      [theme.breakpoints.up('sm')]: {
        marginLeft: drawerWidth,
      },
    }),
  };
});

// Optimized Footer component with reduced height and color coding
const FooterBox = styled(Box)(({ theme, data }) => {
  // Add safety checks for data
  const userRole = data?.users?.role ? JSON.parse(data.users.role) : 0;
  const isAdminOrHigher = userRole === 1 || userRole >= 3;
  
  return {
    width: '100%',
    height: '60px', // Reduced height from default
    background: isAdminOrHigher
      ? 'transparent'
      : 'linear-gradient(135deg, #0EA4D9 0%, #13E9DD 100%)',
    borderRadius: '30px 30px 0 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 'auto',
    position: 'fixed',
    zIndex: 1000,
    bottom: 0,
    [theme.breakpoints.down('sm')]: {
      height: '50px', // Even smaller on mobile
    }
  };
});

// ----------------------------------------------------------------------

// Memoized Layout Components to prevent unnecessary rerenders
const MemoizedHeader = React.memo(({ open, onOpenSidebar, onOpenAdminSidebar, data, location }) => {
  // Only render if data is available and properly structured
  if (!data || !data.users) {
    return null;
  }
  
  // Check if current route is the QR scan time keep route
  const isQRScanRoute = location?.pathname === '/stores/biometric';
  const headerPosition = isQRScanRoute ? 'static' : 'fixed';
  
  return (
    <AppBar position={headerPosition} open={open} data={data} isQRScanRoute={isQRScanRoute}>
      <DashboardNavbar 
        open={open} 
        onOpenSidebar={onOpenSidebar} 
        onOpenAdminSidebar={onOpenAdminSidebar} 
        _data={data} 
      />
    </AppBar>
  );
});

const MemoizedSidebar = React.memo(({ isOpenSidebar, account, onCloseSidebar }) => (
  <DashboardSidebar 
    isOpenSidebar={isOpenSidebar} 
    account={account} 
    onCloseSidebar={onCloseSidebar} 
  />
));

const MemoizedDrawer = React.memo(({ 
  theme, 
  drawerWidth, 
  openUserDrawer, 
  handleDrawerClose, 
  user, 
  onClose, 
  onNavigate, 
  onOpenWorkmateTimein, 
  userData 
}) => (
  <Drawer
    sx={{
      width: drawerWidth,
      [theme.breakpoints.down('sm')]: {
        width: `100%`,
      },
      [theme.breakpoints.up('sm')]: {
        width: drawerWidth,
      },
      flexShrink: 0,
      '& .MuiDrawer-paper': {
        [theme.breakpoints.down('sm')]: {
          width: `100%`,
        },
        [theme.breakpoints.up('sm')]: {
          width: drawerWidth,
        },
      },
    }}
    variant="persistent"
    anchor="right"
    open={openUserDrawer}
  >
    <DrawerHeader sx={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      height: 120,
      background: 'linear-gradient(135deg, #0EA4D9 0%, #13E9DD 100%)',
      color: 'white',
      overflow: 'hidden',
      px: 2,
      borderRadius: '0 0 30px 30px',
    }}>
      <IconButton 
        onClick={handleDrawerClose} 
        sx={{
          color: 'white',
          size: 60,
          position: 'absolute',
          left: 8
        }}
      >
        {theme.direction === 'rtl' ? 
          <ChevronLeftIcon sx={{color: 'white', fontSize: 30}} /> : 
          <ChevronRightIcon sx={{color: 'white', fontSize: 30}} />
        }
      </IconButton>
      
      <Box sx={{
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        pl: 2
      }}>
        <Avatar
          src={user?.image || '/static/mock-images/avatars/avatar_default.jpg'}
          sx={{ 
            width: 50,
            height: 50,
            border: '2px solid white'
          }}
        />
        
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="subtitle2" noWrap>
            {user?.displayName}
          </Typography>
          <Typography variant="caption" noWrap>
            {user?.email}
          </Typography>
        </Box>
      </Box>
    </DrawerHeader>
    <AccountPopover
      open={openUserDrawer}
      onClose={onClose}
      onNavigate={onNavigate}
      onOpenWorkmateTimein={onOpenWorkmateTimein}
      user={userData}
    /> 
    {/* {!isMobile && (
      <DrawerFooter
        sx={{
          background: 'linear-gradient(135deg, #0EA4D9 0%, #13E9DD 100%)',
          bottom: 0,
          position: 'fixed',
          width: '100%',
          height: '50px',
          borderRadius: '30px 30px 0 0'
        }}
      />
    )} */}
  </Drawer>
));

// Memoized Footer Component
const MemoizedFooter = React.memo(({ data }) => {
  // Only render if data is available
  if (!data || !data.users) {
    return null;
  }
  return <FooterBox data={data} />;
});
  
    {/* <Typography 
      variant="caption" 
      sx={{ 
        color: 'white',
        fontSize: '0.75rem',
        fontWeight: 500
      }}
    >
      Powered by Sparkle Timekeeping
    </Typography> */}

export default function DashboardLayout() {
  const theme = useTheme();
  // const socket = useContext(SocketContext)
  const { setName, setRoom } = useContext(MainContext)
  const [openUserDrawer, setOpenUserDrawer] = useState(false)
  const [openAdminDrawer, setOpenAdminDrawer] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notifyToggle, setNotifyToggle] = useState(storage.getScheduleNotify() ?? false)
/*  const {getFcmToken} = useFirebase()*/
/*  const [fCMToken, setFCMToken] = useState(storage.getFCMToken() ?? null);
  const [fCMFailCount, setFCMFailCount] = useState(0);*/
  const [componentMounted, setComponentMounted] = useState(false);
  const [data, setData] = useState({
    users: {
      role: '0' // Initialize with default role to prevent JSON.parse errors
    },
    online: 0,
    employees: [],
  })
  const [user, setUser] = useState(null)
  const { openWorkmateTimein } = useWorkmateTimein();

/*  useEffect( async () => {
    const fetchAndStoreFcmToken = async () => {
      try {
        const user = JSON.parse(await storage.getUser())
        console.log('kuha na: ', user)
        if (!fCMToken) {
          console.log("FCM token is not available, fetching a new one");
          const newFcmToken = await getFcmToken();
  
          if (newFcmToken) {
            storage.storeFCM(newFcmToken);
            setFCMToken(newFcmToken);
            if (user) {
              const currentUserFCMToken = await user_api.get_active_fcm_token(user._id);
              const fcmTokensArray = currentUserFCMToken.data.fcmTokens.map(tokenObj => tokenObj.fcmToken);
              if(!currentUserFCMToken || !fcmTokensArray.includes(fCMToken)) {
                await user_api.subscribe_fcm_token(user._id, fCMToken, 'pwa');
                console.log("Subscribed FCM token for user:", user._id);
              }else{
                console.log("Failed to subscribe user token", user._id);
              }
            }
          } else {
            console.log("Failed to fetch new FCM token");
            return;
          }
        } else {
          console.log("FCM token already exists:", fCMToken);
          if (!storage.getFCMToken()) storage.storeFCM(fCMToken);
          if (user) {
            const currentUserFCMToken = await user_api.get_active_fcm_token(user._id);
            const fcmTokensArray = currentUserFCMToken.data.fcmTokens.map(tokenObj => tokenObj.fcmToken);
            if(!currentUserFCMToken || !fcmTokensArray.includes(fCMToken)) {
              await user_api.subscribe_fcm_token(user._id, fCMToken, 'pwa');
              console.log("Subscribed FCM token for user:", user._id);
            }else{
              console.log("Failed to subscribe user token", user._id);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching or storing FCM token:", error);
      }
    };

    if (notifyToggle === true) {
      const intervalId = setInterval(async () => {
        const existingToken = storage.getFCMToken()
        
        if (existingToken) {
          clearInterval(intervalId); // Stop the interval if token is already in local storage
          return;
        }
  
        if (fCMFailCount > 5) {
          clearInterval(intervalId); // Stop the interval if token is already in local storage
          return;
        }
        
        fetchAndStoreFcmToken();
        setFCMFailCount((prev) => prev + 1);
      }, 15000); // Fetch every 5 seconds
      fetchAndStoreFcmToken();
    }
  }, [fCMToken, notifyToggle])*/

  const load = async () => {
    setComponentMounted(false)
    const token = await storage.getToken()
    if (!token) {
      return navigate('/login')
    }
    setComponentMounted(true)
    const local_user = await storage.getUser()
    if (!local_user) return;
    const user = JSON.parse(local_user)
    setUser(user)
    setData((p) => ({ ...p, users: user }))
  }

  const check_login = async () => {
    const token = await storage.getToken()
    if (!token) {
      setIsLoggedIn(false)
      navigate('/login')
      return
    }
    try {
      const decodedToken = jwt_decode(token)
      const currentTime = Date.now() / 1000
      if (decodedToken.exp < currentTime) {
        await storage.remove()
        setIsLoggedIn(false)
        navigate('/login')
        return
      }
      setIsLoggedIn(true)
    } catch (error) {
      await storage.remove()
      setIsLoggedIn(false)
      navigate('/login')
    }
  }

  useEffect(() => {
    load()
    check_login()
  }, [navigate])

  useEffect(() => {
    setComponentMounted(true)
    return () => {
      setComponentMounted(false)
    }
  }, [])

  // Handle back button and navigation
  useEffect(() => {
    const handleBackButton = (event) => {
      if (openUserDrawer || openAdminDrawer) {
        event.preventDefault();
        if (openUserDrawer) {
          handleDrawerClose();
        } else if (openAdminDrawer) {
          handleAdminDrawerClose();
        }
      }
    };

    const handlePopState = (event) => {
      if (openUserDrawer || openAdminDrawer) {
        event.preventDefault();
        if (openUserDrawer) {
          handleDrawerClose();
        } else if (openAdminDrawer) {
          handleAdminDrawerClose();
        }
        window.history.pushState(null, document.title, window.location.href);
        return;
      }
    };

    document.addEventListener('backbutton', handleBackButton);
    window.addEventListener('popstate', handlePopState);
    window.history.pushState(null, document.title, window.location.href);

    return () => {
      document.removeEventListener('backbutton', handleBackButton);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [openUserDrawer, openAdminDrawer]);

  // Memoized handlers to prevent unnecessary rerenders
  const handleDrawerOpen = useCallback(() => {
    setOpenUserDrawer(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setOpenUserDrawer(false);
  }, []);

  const handleAdminDrawerOpen = useCallback(() => {
    setOpenAdminDrawer(true);
  }, []);

  const handleAdminDrawerClose = useCallback(() => {
    setOpenAdminDrawer(false);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    storage.setScheduleNotify(notifyToggle)
  }, [notifyToggle]);

  const handleNotifyToggle = useCallback((value) => {
    setNotifyToggle(value)
  }, []);

  // Memoized data for child components
  const memoizedData = useMemo(() => data, [data]);
  const memoizedUser = useMemo(() => user, [user]);

  // Check if current route is QR scan route for layout modifications
  const isQRScanRoute = location?.pathname === '/stores/biometric';

  return (
    <>
      {isQRScanRoute ? (
        // Simplified layout for QR scan route
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <NotificationComponent />
          <MemoizedHeader 
            open={openUserDrawer}
            onOpenSidebar={handleDrawerOpen}
            onOpenAdminSidebar={handleAdminDrawerOpen}
            data={memoizedData}
            location={location}
          />
          <NotificationDialog open={dialogOpen} onClose={handleCloseDialog} toggle={notifyToggle} onToggle={handleNotifyToggle} />  
          {data.users.role && (JSON.parse(data.users.role) === 1 || JSON.parse(data.users.role) >= 3) ? (
            <MemoizedSidebar 
              isOpenSidebar={openAdminDrawer} 
              account={data.users} 
              onCloseSidebar={handleAdminDrawerClose} 
            />
          ) : (
            ''
          )}
          <div style={{ 
            flexGrow: 1, 
            width: '100%', 
            padding: '8px',
            paddingTop: '80px', // Add top padding to prevent header overlap
            overflow: 'auto'
          }}>
            <Suspense fallback={<div>Loading...</div>}>
              <Outlet />
            </Suspense>
          </div>
          <MemoizedDrawer
            theme={theme}
            drawerWidth={drawerWidth}
            openUserDrawer={openUserDrawer}
            handleDrawerClose={handleDrawerClose}
            user={memoizedUser}
            onClose={handleDrawerClose}
            onNavigate={handleDrawerClose}
            onOpenWorkmateTimein={openWorkmateTimein}
            userData={data.users}
          />
        </div>
      ) : (
        // Original layout for all other routes
        <RootStyle>
          <NotificationComponent />
          {/*<NewFeatureDialog />*/}
          <MemoizedHeader 
            open={openUserDrawer}
            onOpenSidebar={handleDrawerOpen}
            onOpenAdminSidebar={handleAdminDrawerOpen}
            data={memoizedData}
            location={location}
          />
          <NotificationDialog open={dialogOpen} onClose={handleCloseDialog} toggle={notifyToggle} onToggle={handleNotifyToggle} />  
          {data.users.role && (JSON.parse(data.users.role) === 1 || JSON.parse(data.users.role) >= 3) ? (
            <MemoizedSidebar 
              isOpenSidebar={openAdminDrawer} 
              account={data.users} 
              onCloseSidebar={handleAdminDrawerClose} 
            />
          ) : (
            ''
          )}
           <Main open={openUserDrawer} isQRScanRoute={isQRScanRoute}>
           {/* <DrawerHeader /> */}
            <MainStyle style={{ minHeight: componentMounted ? '100vh' : '200vh' }}>
              {/* <Detector
                render={({online}) =>
                  !online ? (
                    <>
                      <Box
                        component="img"
                        src="/static/illustrations/offline.gif"
                        sx={{height: 300, mx: 'auto', my: {xs: 0, sm: 0}}}
                      />
                      <Typography variant="h4" sx={{textAlign: 'center'}}>
                        No Internet Connection
                      </Typography>
                    </>
                  ) : (
                    <Outlet />
                  )
                }
              /> */}
              {/* <DrawerHeader /> */}
              <Box sx={{ 
                mt: 11,
                mb: 2,
                pl: 1, 
                pr: 1,
                // Removed excessive padding to reduce gap
                pb: 2
              }}>
                <Suspense fallback={<div>Loading...</div>}>
                  <Outlet />
                </Suspense>
              </Box>
            </MainStyle>
          </Main>
          <MemoizedFooter data={memoizedData} />
          <MemoizedDrawer
            theme={theme}
            drawerWidth={drawerWidth}
            openUserDrawer={openUserDrawer}
            handleDrawerClose={handleDrawerClose}
            user={memoizedUser}
            onClose={handleDrawerClose}
            onNavigate={handleDrawerClose}
            onOpenWorkmateTimein={openWorkmateTimein}
            userData={data.users}
          />
        </RootStyle>
      )}
    </>
  )
}
